import { Injectable, inject, signal } from '@angular/core';

import { ConfirmationLockService } from './confirmation-lock.service';

export interface ConfirmationOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConfirmationService {
  showModal = signal(false);
  options = signal<ConfirmationOptions | null>(null);

  /**
   * BE-833: резолвер текущего диалога хранится per-call, а не в общем Subject.
   * С общим Subject резолв одного вызова резолвил все накопленные промисы —
   * подтверждение одного действия выполняло заодно и все соседние.
   */
  private resolveCurrent: ((result: boolean) => void) | null = null;

  private readonly lock = inject(ConfirmationLockService);

  /**
   * BE-1237: слой, занятый под ЭТОТ диалог. Держим handle сами, а не поручаем окну:
   * окно диалога живёт под @if и снимало бы слой по уничтожению — то есть на проход
   * change detection позже, чем settle() резолвит промис, и следующее подтверждение
   * из await-продолжения отбивалось бы. Плюс адресность: settle() выполняется и когда
   * своего диалога не было вовсе (холостой клик), и тогда снимать нечего — а массовое
   * снятие убрало бы слой чужого, видимого на экране окна.
   */
  private releaseLayer: (() => void) | null = null;

  confirm(opts: ConfirmationOptions): Promise<boolean> {
    // Пока открыто любое подтверждение — своё или локальное, — второе не поднимаем:
    // оно перетёрло бы текст первого, и пользователь ответил бы не на тот вопрос.
    // Сторожим по resolveCurrent, а не по showModal: showModal — публичный сигнал,
    // и запись в него снаружи иначе подвесила бы незавершённый вызов навсегда.
    if (this.resolveCurrent || this.lock.locked) {
      // BE-1237: текст и дедуп подсказки живут в реестре слоя — отбить попытку может
      // и локальное окно, а тост на смешанную серию обязан остаться одним.
      this.lock.noticeBlocked();
      return Promise.resolve(false);
    }

    this.lock.resetNotice();
    // Слой берём ДО показа и на каждый вызов заново: @if не пересоздаёт вью, когда
    // showModal успевает сходить false→true внутри одного тика (цепочка подтверждений).
    this.releaseLayer = this.lock.acquire();
    this.options.set(opts);
    this.showModal.set(true);

    return new Promise<boolean>((resolve) => {
      this.resolveCurrent = resolve;
    });
  }

  onConfirm(): void {
    this.settle(true);
  }

  onCancel(): void {
    this.settle(false);
  }

  /**
   * Закрывает диалог и резолвит ровно тот вызов, который его открыл.
   * Состояние гасится до resolve(), поэтому потребитель может открыть следующий
   * диалог прямо в своём await-продолжении. Повторный вызов (двойной клик по
   * «Подтвердить») уже не находит резолвера и молчит.
   */
  private settle(result: boolean): void {
    const resolve = this.resolveCurrent;
    this.resolveCurrent = null;
    this.lock.resetNotice();
    this.showModal.set(false);
    this.options.set(null);
    // BE-1237: снимаем ровно свой слой и синхронно — до resolve(), чтобы потребитель мог
    // открыть следующий диалог прямо в await-продолжении. Без своего диалога здесь null,
    // и холостой клик не трогает слой чужого окна.
    this.releaseLayer?.();
    this.releaseLayer = null;
    resolve?.(result);
  }
}
